import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { appActions } from '../../../store';
import LoadingScreen from '../../shared/LoadingScreen/LoadingScreen';
import ChooseSceneModal from '../../shared/ChooseSceneModal/ChooseSceneModal';
import RoomscenesPopup from '../RoomscenesPopup/RoomscenesPopup';
import SurfaceMenuItem from '../../shared/SurfaceMenuItem/SurfaceMenuItem';
import styles from './Visualizer.module.css';
import zoomreset from './assets/zoomreset.svg';
import undo from './assets/undo.svg';
import redo from './assets/redo.svg';

// Generate swatch URL using patternColors currentId values
const getSwatchUrl = (chosenPattern, patternColors, imageSize = 1024) => {
    if (!chosenPattern || !patternColors || patternColors.length === 0) return null;

    const defaultRoomSceneSpecifier = chosenPattern.defaultRoomSceneSpecifier;
    if (!defaultRoomSceneSpecifier) return null;

    const cutLoopString = defaultRoomSceneSpecifier
        .split(',')
        .map(item => item.trim() === 'L' ? '@LOOP' : '@CUT')
        .join(',');

    const selString = patternColors
        .map(color => `!${color.currentId}`)
        .join(',');

    return `https://renderfarm2.chameleonpower.com/proxy/getImage.ashx?spec=${chosenPattern.specifier}(APPLY=(SCALE=1,COLORS=(${selString}),TEXTURES=(${cutLoopString})),FOCUS=(@SIZE[1]/(@PATRES[1]|@RES[1])))&size=${imageSize}&extra=(SIZE=(?,(@SIZE[1]*@PATRES[2]/@PATRES[1])))`;
};

// Generate room view URL using roomscene, floor product, and pattern colors
const getRoomViewUrl = (selectedRoomscene, selectedFloorProduct, chosenPattern, patternColors, imageSize = 2000) => {
    if (!selectedRoomscene || !chosenPattern || !patternColors || patternColors.length === 0) return null;

    const defaultRoomSceneSpecifier = chosenPattern.defaultRoomSceneSpecifier;
    if (!defaultRoomSceneSpecifier) return null;

    const cutLoopString = defaultRoomSceneSpecifier
        .split(',')
        .map(item => item.trim() === 'L' ? '@LOOP' : '@CUT')
        .join(',');

    const selString = patternColors
        .map(color => `!${color.currentId}`)
        .join(',');

    return `https://renderfarm2.chameleonpower.com/proxy/getImage.ashx?specifier=${selectedRoomscene.specifier}(qfactor=(10,4))(SIZE=(2000,0))(APPLY=(${chosenPattern.specifier}(APPLY=(SCALE=1,COLORS=(${selString}),TEXTURES=(${cutLoopString})))))&size=1024`;
};

export default function Visualizer() {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [history, setHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [showSceneModal, setShowSceneModal] = useState(false);
    const [productType, setProductType] = useState('carpet');
    const [showRoomscenesPopup, setShowRoomscenesPopup] = useState(false);
    const [roomImageUrl, setRoomImageUrl] = useState(null);
    const isUndoRedoAction = useRef(false);
    
    const activeProfile = useSelector(state => state.app.activeProfile);
    const productSelection = useSelector(state => state.app.productSelection);
    const roomscene = useSelector(state => state.app.roomscene);
    const style = useSelector(state => state.app.style);
    const size = useSelector(state => state.app.size);
    const currentView = useSelector(state => state.app.currentView);
    const chosenPattern = useSelector(state => state.app.chosenPattern);
    const patternColors = useSelector(state => state.app.patternColors);
    const selectedRoomscene = useSelector(state => state.app.selectedRoomscene);
    const selectedFloorProduct = useSelector(state => state.app.selectedFloorProduct);
    
    const lastPatternId = useRef(null);
    
    const viewMode = currentView || 'swatch';
    const imageSize = zoom > 1 ? 3000 : 1500;
    const imageUrl = getSwatchUrl(chosenPattern, patternColors, imageSize);

    // Clear history when pattern changes
    useEffect(() => {
        if (chosenPattern?.id !== lastPatternId.current) {
            lastPatternId.current = chosenPattern?.id || null;
            setHistory([]);
            setHistoryIndex(-1);
        }
    }, [chosenPattern]);

    // Build room view image URL when roomscene, floor product, or pattern colors change
    useEffect(() => {
        if (productType === 'room') {
            const url = getRoomViewUrl(selectedRoomscene, selectedFloorProduct, chosenPattern, patternColors, imageSize);
            setRoomImageUrl(url);
            if (url) {
                setIsLoading(true);
            }
        }
    }, [productType, selectedRoomscene, selectedFloorProduct, chosenPattern, patternColors, imageSize]);

    // Update Redux with current image URL
    useEffect(() => {
        const currentUrl = productType === 'room' ? roomImageUrl : imageUrl;
        if (currentUrl) {
            dispatch(appActions.setCurrentImageUrl(currentUrl));
        }
    }, [imageUrl, roomImageUrl, productType, dispatch]);

    // Show loading when imageUrl changes
    useEffect(() => {
        if (imageUrl && productType === 'carpet') {
            setIsLoading(true);
        }
    }, [imageUrl, productType]);

    // Track history when patternColors changes
    useEffect(() => {
        if (isUndoRedoAction.current) {
            isUndoRedoAction.current = false;
            return;
        }
        if (!patternColors || patternColors.length === 0) return;

        // Deep copy patternColors for history
        const currentState = { patternColors: JSON.parse(JSON.stringify(patternColors)) };
        const lastState = history[historyIndex];
        
        // Check if colors actually changed
        if (lastState && JSON.stringify(lastState.patternColors) === JSON.stringify(currentState.patternColors)) {
            return;
        }

        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(currentState);
        
        if (newHistory.length > 50) {
            newHistory.shift();
        }
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
    }, [patternColors]);

    const handleImageLoad = () => setIsLoading(false);
    const handleImageError = () => setIsLoading(false);

    const handleViewModeChange = (mode) => {
        if (mode !== viewMode) {
            setIsLoading(true);
            dispatch(appActions.setCurrentView(mode));
        }
    };

    const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
    const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
    const handleZoomReset = () => {
        setZoom(1);
        setPanPosition({ x: 0, y: 0 });
    };

    const handleMouseDown = (e) => {
        if (zoom > 1) {
            setIsDragging(true);
            setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
            e.preventDefault();
        }
    };

    const handleMouseMove = (e) => {
        if (!isDragging || zoom <= 1) return;
        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;
        const maxPan = (zoom - 1) * 500;
        setPanPosition({
            x: Math.max(-maxPan, Math.min(maxPan, newX)),
            y: Math.max(-maxPan, Math.min(maxPan, newY))
        });
    };

    const handleMouseUp = () => setIsDragging(false);
    const handleMouseLeave = () => setIsDragging(false);

    const handleUndo = () => {
        if (historyIndex > 0) {
            const previousState = history[historyIndex - 1];
            isUndoRedoAction.current = true;
            dispatch(appActions.setPatternColors(previousState.patternColors));
            setHistoryIndex(historyIndex - 1);
        }
    };

    const handleRedo = () => {
        if (historyIndex < history.length - 1) {
            const nextState = history[historyIndex + 1];
            isUndoRedoAction.current = true;
            dispatch(appActions.setPatternColors(nextState.patternColors));
            setHistoryIndex(historyIndex + 1);
        }
    };

    const canUndo = historyIndex > 0;
    const canRedo = historyIndex < history.length - 1;

    const allSlotsFilled = () => {
        if (!activeProfile || !activeProfile.descriptor) return false;
        const slots = activeProfile.descriptor.split(',').map(s => s.trim());
        const requiredSlots = slots.length;
        if (productSelection.length < requiredSlots) return false;
        for (let i = 0; i < requiredSlots; i++) {
            if (!productSelection[i] || productSelection[i] === -1) return false;
        }
        return true;
    };

    const showToggle = allSlotsFilled();
    const isUserUploadedScene = roomscene && roomscene.includes('_USER_');

    const handleSceneSelect = (specifier) => {
        dispatch(appActions.setRoomscene(specifier));
        setShowSceneModal(false);
    };

    const handleEditImage = () => {
        localStorage.setItem('current_selection', window.location.href);
        window.location.href = `/im.aspx?spec=${encodeURIComponent(roomscene)}`;
    };

    const handleProductTypeChange = (type) => {
        setProductType(type);
        if (type === 'room' && !selectedRoomscene) {
            setShowRoomscenesPopup(true);
        }
    };

    const handleRoomsceneSelect = (scene) => {
        console.log('Selected roomscene:', scene);
        dispatch(appActions.setSelectedRoomscene(scene));
        setShowRoomscenesPopup(false);
        setIsLoading(true);
    };

    const displayImageUrl = productType === 'room' ? roomImageUrl : imageUrl;

    return (
        <div className={styles.visualizer}>
            <LoadingScreen isOpen={isLoading} />
            <ChooseSceneModal 
                isOpen={showSceneModal} 
                onClose={() => setShowSceneModal(false)}
                onSceneSelect={handleSceneSelect}
            />
            <RoomscenesPopup
                isOpen={showRoomscenesPopup}
                onClose={() => setShowRoomscenesPopup(false)}
                onSceneSelect={handleRoomsceneSelect}
            />

            <div className={styles.topRightControls}>
                {productType === 'room' && (
                    <button
                        className={styles.changeSceneButton}
                        onClick={() => setShowRoomscenesPopup(true)}
                        aria-label="Change scene"
                    >
                        CHANGE SCENE
                    </button>
                )}
                <div className={styles.productTypeToggle}>
                    <button
                        className={`${styles.productTypeOption} ${productType === 'room' ? styles.active : ''}`}
                        onClick={() => handleProductTypeChange('room')}
                        aria-label="Switch to room mode"
                    >
                        Room
                    </button>
                    <button
                        className={`${styles.productTypeOption} ${productType === 'carpet' ? styles.active : ''}`}
                        onClick={() => handleProductTypeChange('carpet')}
                        aria-label="Switch to carpet mode"
                    >
                        Swatch
                    </button>
                </div>
            </div>

            {viewMode === 'room' && showToggle && (
                <div className={styles.roomButtonsContainer}>
                    {isUserUploadedScene && (
                        <button 
                            className={styles.editImageButton}
                            onClick={handleEditImage}
                            aria-label="Edit image"
                        >
                            Edit Image
                        </button>
                    )}
                    <button 
                        className={styles.changeRoomButton}
                        onClick={() => setShowSceneModal(true)}
                        aria-label="Change room"
                    >
                        Change Room
                    </button>
                </div>
            )}

            {showToggle && (
                <div className={styles.toggleContainer}>
                    <button 
                        className={`${styles.toggleOption} ${viewMode === 'swatch' ? styles.active : ''}`}
                        onClick={() => handleViewModeChange('swatch')}
                        aria-label="Switch to swatch view"
                    >
                        Swatch
                    </button>
                    <button 
                        className={`${styles.toggleOption} ${viewMode === 'room' ? styles.active : ''}`}
                        onClick={() => handleViewModeChange('room')}
                        aria-label="Switch to room view"
                    >
                        Room
                    </button>
                </div>
            )}

            <div 
                className={styles.imageContainer}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
            >
                {displayImageUrl ? (
                    <img 
                        src={displayImageUrl} 
                        alt={productType === 'room' ? 'Room view' : 'Product swatch'}
                        className={styles.visualizerImage}
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                        draggable={false}
                        style={{ 
                            display: isLoading ? 'none' : 'block',
                            transform: `scale(${zoom}) translate(${panPosition.x / zoom}px, ${panPosition.y / zoom}px)`,
                            cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                        }}
                    />
                ) : (
                    <div className={styles.placeholder}>
                        <p>{productType === 'room' ? 'Select a room scene to begin' : 'Select a pattern to begin'}</p>
                    </div>
                )}
            </div>

            <SurfaceMenuItem />

            <div className={styles.controlButtons}>
                <button className={styles.controlButton} onClick={handleZoomIn} aria-label="Zoom in" title="Zoom in">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2"/>
                        <line x1="8" y1="5" x2="8" y2="11" stroke="currentColor" strokeWidth="2"/>
                        <line x1="5" y1="8" x2="11" y2="8" stroke="currentColor" strokeWidth="2"/>
                        <line x1="13" y1="13" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                </button>
                <button className={styles.controlButton} onClick={handleZoomOut} aria-label="Zoom out" title="Zoom out">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2"/>
                        <line x1="5" y1="8" x2="11" y2="8" stroke="currentColor" strokeWidth="2"/>
                        <line x1="13" y1="13" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                </button>
                <button 
                    className={`${styles.controlButton} ${zoom === 1 ? styles.disabled : ''}`}
                    onClick={handleZoomReset}
                    disabled={zoom === 1}
                    aria-label="Reset zoom"
                    title="Reset zoom"
                >
                    <img src={zoomreset} width="24" height="24" alt="zoom reset"/>
                </button>
                <button 
                    className={`${styles.controlButton} ${!canUndo ? styles.disabled : ''}`}
                    onClick={handleUndo}
                    disabled={!canUndo}
                    aria-label="Undo"
                    title="Undo"
                >
                    <img src={undo} width="24" height="24" alt="undo"/>
                </button>
                <button 
                    className={`${styles.controlButton} ${!canRedo ? styles.disabled : ''}`}
                    onClick={handleRedo}
                    disabled={!canRedo}
                    aria-label="Redo"
                    title="Redo"
                >
                    <img src={redo} width="24" height="24" alt="redo"/>
                </button>
            </div>
        </div>
    );
}
